// Turns what an AI wrote (a server task answer or an assistant's proposal, both by name: "200 g flour")
// into Chefi data on the device: names become the person's own ingredients (matched with ingredientMatch,
// or new ingredients that still need their diets checked), units become seed units, steps get their links.
import { fromNumber, ONE } from './quantity.ts';
import { buildMatcher, matchName, unitFromText, type Matchable } from './ingredientMatch.ts';
import type { Id, Ingredient, IngredientLine, Recipe } from '../types';

export type DraftLine = { name: string; amount: number | null; unit: string | null };
export type RecipeDraft = { title: string; servings: number | null; durationMin: number | null; ingredients: DraftLine[]; steps: { text: string; ingredients: string[] }[] };

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Resolves names against `known` (built-ins and the person's own), creating ingredients for the rest. */
export function resolver(known: Record<Id, Ingredient>, newId: () => Id) {
  const matcher = buildMatcher(Object.values(known) as Matchable[]);
  const created: Ingredient[] = [];
  const byName = new Map<string, Id>();
  const resolve = (name: string): Id => {
    const key = name.trim().toLowerCase();
    const hit = byName.get(key) ?? matchName(name, matcher);
    if (hit) {
      byName.set(key, hit);
      return hit;
    }
    const ing: Ingredient = { id: newId(), title: capital(name.trim()), aisle: 'other', notDiet: [], dietsChecked: false, synonyms: [], explainer: '', builtIn: false };
    created.push(ing);
    byName.set(key, ing.id);
    return ing.id;
  };
  return { resolve, created };
}

/** One draft line as an ingredient line. A unit the app does not know is dropped rather than guessed. */
export function toLine(l: DraftLine, resolve: (name: string) => Id): IngredientLine {
  const unit = l.amount ? unitFromText(l.unit) : null;
  return { ingredientId: resolve(l.name), amount: l.amount ? fromNumber(l.amount) : ONE, unit };
}

/**
 * A draft recipe → a Recipe plus the ingredients it needed to create. A step lists the ingredients the draft
 * says it uses; the recipe amount goes to the first step that uses an ingredient (later steps reuse it).
 */
export function draftToRecipe(d: RecipeDraft, known: Record<Id, Ingredient>, newId: () => Id, now: number): { recipe: Recipe; created: Ingredient[] } {
  const { resolve, created } = resolver(known, newId);
  const ingredients: IngredientLine[] = [];
  for (const l of d.ingredients) {
    const line = toLine(l, resolve);
    if (!ingredients.some(x => x.ingredientId === line.ingredientId)) ingredients.push(line);
  }
  // A name a step uses that the ingredient list forgot still has to be bought: list it (amount 1).
  for (const s of d.steps) for (const name of s.ingredients) {
    const id = resolve(name);
    if (!ingredients.some(l => l.ingredientId === id)) ingredients.push({ ingredientId: id, amount: ONE, unit: null });
  }
  const used = new Set<Id>();
  const steps = d.steps.map(s => {
    const lines: IngredientLine[] = [];
    for (const name of s.ingredients) {
      const id = resolve(name);
      const line = ingredients.find(l => l.ingredientId === id);
      if (!line || used.has(id) || lines.some(l => l.ingredientId === id)) continue;
      used.add(id);
      lines.push(line);
    }
    return { id: newId(), title: s.text, explainer: '', ingredients: lines, utensils: [], timers: [] };
  });
  const recipe: Recipe = {
    id: newId(), title: d.title || 'New recipe', explainer: '', servings: Math.max(1, Math.round(d.servings ?? 2)), durationMin: Math.max(0, Math.round(d.durationMin ?? 0)), priceCents: 0,
    ingredients, utensils: [], steps, updatedAt: now,
  };
  return { recipe, created };
}
